import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
  Wallet,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/context/FeaturesContext";
import { useBranch } from "@/context/BranchContext";
import ClinicHealthCalculator from "@/components/clinic-health-calculator/ClinicHealthCalculator";

const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 Days" },
  { value: "mtd", label: "Month to Date" },
  { value: "30d", label: "Last 30 Days" },
  { value: "custom", label: "Custom" },
];

const STREAM_COLORS = ["bg-teal-600", "bg-emerald-400", "bg-blue-500", "bg-amber-500", "bg-slate-400", "bg-violet-500"];
const COMMISSION_PAGE_SIZE = 10;

export default function Reports() {
  const { activeClinic } = useAuth();
  const { hasFeature, plan } = useFeatures();
  const { reportId } = useParams();
  const canSeeReportsDashboard = hasFeature("reports");
  const canSeeHealthCalculator = hasFeature("clinic_health_calculator");

  if (canSeeHealthCalculator && (plan === "solo" || !canSeeReportsDashboard)) {
    return <ClinicHealthCalculator />;
  }

  if (reportId === "commissions") {
    return <CommissionBreakdown activeClinic={activeClinic} />;
  }

  return <ReportsDashboard activeClinic={activeClinic} />;
}

function ReportsDashboard({ activeClinic }) {
  const initialFilters = useMemo(() => readReportFiltersFromUrl(), []);
  const { activeBranchId, loading: branchLoading } = useBranch();
  const branchId = activeBranchId || "";
  const [period, setPeriod] = useState(initialFilters.period);
  const [customRange, setCustomRange] = useState(initialFilters.customRange);
  const [activeTab, setActiveTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const effectivePeriod = useMemo(() => {
    if (period !== "custom") return period;
    return `custom:${customRange.start}:${customRange.end}`;
  }, [customRange.end, customRange.start, period]);

  const commissionBreakdownPath = useMemo(
    () => buildReportPath("/reports/commissions", effectivePeriod, branchId),
    [branchId, effectivePeriod]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (!branchId) {
      setDashboard(null);
      setLoading(Boolean(branchLoading));
      setError(branchLoading ? null : "No active branch selected.");
      return;
    }
    const { data, error: rpcError } = await supabase.rpc("reports_page_dashboard", {
      p_period: effectivePeriod,
      p_branch_id: branchId,
    });
    if (rpcError) {
      setError(rpcError.message || "Could not load reports.");
      setLoading(false);
      return;
    }
    setDashboard(data || {});
    setLoading(false);
  }, [branchId, branchLoading, effectivePeriod]);

  useEffect(() => { load(); }, [load]);

  const currency = dashboard?.currency || activeClinic?.currency || "PHP";
  const revenue = dashboard?.revenue || {};

  if (loading && !dashboard) {
    return (
      <div data-testid="reports-loading" className="rounded-lg border border-slate-200 bg-slate-50 p-5 text-[13px] text-slate-500">
        Loading reports...
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="reports-error" className="rounded-lg border border-rose-200 bg-rose-50 p-5 text-[13px] text-rose-700">
        <div className="flex items-center gap-2 font-bold">
          <AlertTriangle className="h-4 w-4" strokeWidth={2.25} />
          Reports could not load
        </div>
        <p className="mt-1">{error}</p>
      </div>
    );
  }

  return (
    <main data-testid="reports-page" className="mx-auto max-w-[1180px] space-y-5 text-slate-950">
      <header className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[13px] font-semibold text-slate-500">
              {activeClinic?.clinic_name || activeClinic?.name || "Clinic"} · {dashboard?.period_label || "Month to date"}
            </p>
            <h1 className="mt-1 text-[30px] font-bold tracking-tight text-slate-950">Reports</h1>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-[13px] font-semibold text-slate-700 transition hover:bg-slate-50"
              aria-label="Export reports"
            >
              <Download className="h-4 w-4" strokeWidth={2.25} />
              Export
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <FilterBar period={period} onPeriodChange={setPeriod} />
          <p className="text-[12px] font-semibold text-slate-500">{dashboard?.period_label || "Month to date"}</p>
        </div>
      </header>

      {period === "custom" ? (
        <CustomRangeInputs customRange={customRange} onChange={setCustomRange} />
      ) : null}

      <ReportsTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "overview" ? (
        <div data-testid="reports-overview-tab" className="space-y-5">
          {Number(revenue.ar_60_plus_patient_count || 0) > 0 ? (
            <WarningStrip
              count={revenue.ar_60_plus_patient_count}
              amount={revenue.ar_60_plus_centavos}
              currency={currency}
            />
          ) : null}

          <SectionLabel>Revenue</SectionLabel>
          <div className="grid gap-3 md:grid-cols-4">
            <KpiCard title="Total collected" value={formatMoney(revenue.total_collected_centavos, currency)} sub="+12% vs last period" tone="positive" />
            <KpiCard title="Production (work done)" value={formatMoney(revenue.production_centavos, currency)} sub="Committed, not yet collected" />
            <KpiCard title="Outstanding AR" value={formatMoney(revenue.outstanding_ar_centavos, currency)} sub="+8% vs last period" tone="warning" />
            <KpiCard title="HMO receivables" value={formatMoney(revenue.hmo_receivables_centavos, currency)} sub="Insurer owes, separate bucket" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <ProductionByDoctor rows={dashboard?.production_by_doctor} currency={currency} />
            <PaymentMethodMix rows={dashboard?.payment_method_mix} currency={currency} />
          </div>

          <SectionLabel>AR & Collections</SectionLabel>
          <div className="grid gap-4 lg:grid-cols-2">
            <OpenPatientBalances rows={dashboard?.open_patient_balances} currency={currency} />
            <HmoReceivables rows={dashboard?.hmo_receivables_by_insurer} currency={currency} />
          </div>

          <SectionLabel>Clinical & Conversion</SectionLabel>
          <div className="grid gap-4 lg:grid-cols-2">
            <TreatmentPlanFunnel data={dashboard?.treatment_plan_funnel} currency={currency} />
            <RecallRetention data={dashboard?.recall_retention} />
          </div>

          <SectionLabel>Orthodontics</SectionLabel>
          <OrthodonticsSection data={dashboard?.orthodontics} currency={currency} />

          <SectionLabel>Doctor Commissions</SectionLabel>
          <DoctorCommissions rows={dashboard?.doctor_commissions} currency={currency} breakdownPath={commissionBreakdownPath} />

          <SectionLabel>Procedure Mix</SectionLabel>
          <ProcedureMix rows={dashboard?.procedure_mix} currency={currency} />
        </div>
      ) : (
        <CashflowTab
          period={effectivePeriod}
          branchId={branchId}
          currency={currency}
        />
      )}
    </main>
  );
}

function ReportsTabs({ activeTab, onChange }) {
  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "cashflow", label: "Cashflow" },
  ];
  return (
    <div className="border-b border-slate-700 bg-[#1f201d] px-4" data-testid="reports-tabs">
      <div className="flex h-12 items-end gap-8">
        {tabs.map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              data-testid={`reports-tab-${tab.key}`}
              onClick={() => onChange(tab.key)}
              className={`relative h-full px-1 text-[14px] font-semibold transition ${active ? "text-teal-500" : "text-slate-100/80 hover:text-white"}`}
            >
              {tab.label}
              {active ? <span className="absolute inset-x-0 bottom-0 h-[2px] bg-teal-500" /> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CashflowTab({ period, branchId, currency }) {
  const navigate = useNavigate();
  const [granularity, setGranularity] = useState("monthly");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc("reports_cashflow", {
      p_period: period,
      p_branch_id: branchId,
      p_granularity: granularity,
    });
    if (rpcError) {
      setError(rpcError.message || "Could not load cashflow.");
      setLoading(false);
      return;
    }
    setPayload(data || {});
    setLoading(false);
  }, [branchId, granularity, period]);

  useEffect(() => { load(); }, [load]);

  if (loading && !payload) {
    return <div data-testid="reports-cashflow-loading" className="rounded-lg border border-slate-200 bg-slate-50 p-5 text-[13px] text-slate-500">Loading cashflow...</div>;
  }

  if (error) {
    return (
      <div data-testid="reports-cashflow-error" className="rounded-lg border border-rose-200 bg-rose-50 p-5 text-[13px] text-rose-700">
        <div className="flex items-center gap-2 font-bold"><AlertTriangle className="h-4 w-4" strokeWidth={2.25} />Cashflow could not load</div>
        <p className="mt-1">{error}</p>
      </div>
    );
  }

  const totals = payload?.totals || {};
  const net = Number(totals.net_cashflow_centavos || 0);
  const clinicWideExcluded = Number(totals.clinic_wide_excluded_centavos ?? payload?.clinic_wide_excluded_centavos ?? 0);
  const branchFiltered = Boolean(branchId);

  return (
    <div data-testid="reports-cashflow-tab" className="space-y-5">
      <section className="grid gap-3 md:grid-cols-4">
        <CashflowKpi title="Total Cash In" value={formatMoney(totals.cash_in_centavos, currency)} sub="Collected in this period" />
        <CashflowKpi title="Cash Out" value={formatMoney(totals.cash_out_centavos, currency)} sub="Expenses + lab costs" />
        <CashflowKpi title="Net Cashflow" value={formatMoney(net, currency)} sub={net >= 0 ? "Positive this period" : "Negative this period"} tone={net >= 0 ? "positive" : "negative"} />
        <CashflowKpi title="PDC clearing next 30d" value={formatMoney(totals.pdc_next_30_centavos, currency)} sub={`${(payload?.pdc_schedule || []).length} checks pending`} />
      </section>

      {branchFiltered && clinicWideExcluded > 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] font-semibold text-slate-500">
          Branch filter applies to Cash In and branch-tagged expenses; {formatMoney(clinicWideExcluded, currency)} of clinic-wide expenses are not shown for this branch.
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="cashflow-edit-overhead" type="button" onClick={() => navigate("/expenses")} className="inline-flex h-10 items-center gap-2 rounded-md bg-teal-700 px-4 text-[13px] font-bold text-white hover:bg-teal-800">
          <Wallet className="h-4 w-4" strokeWidth={2.25} />Manage expenses
        </button>
      </div>

      <CashflowChart payload={payload} granularity={granularity} onGranularityChange={setGranularity} currency={currency} />

      <div className="grid gap-4 lg:grid-cols-2">
        <BreakdownPanel title="Cash In Breakdown" rows={payload?.cash_in_breakdown} currency={currency} />
        <BreakdownPanel title="Cash Out Breakdown" rows={payload?.cash_out_breakdown} currency={currency} />
      </div>

      <PdcSchedule payload={payload} currency={currency} />
    </div>
  );
}

function CashflowKpi({ title, value, sub, tone }) {
  const valueClass = tone === "positive" ? "text-teal-600" : tone === "negative" ? "text-rose-600" : "text-slate-950";
  return (
    <article className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-[12px] font-bold text-slate-500">
        <span>{title}</span>
      </div>
      <div className={`mt-3 text-[26px] font-bold tracking-tight ${valueClass}`}>{value}</div>
      <div className="mt-2 text-[12px] font-semibold text-slate-500">{sub}</div>
    </article>
  );
}

function CashflowChart({ payload, granularity, onGranularityChange, currency }) {
  const rows = Array.isArray(payload?.chart) ? payload.chart : [];
  const max = Math.max(1, ...rows.flatMap((row) => [Number(row.cash_in_centavos || 0), Number(row.cash_out_centavos || 0)]));
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[16px] font-bold tracking-tight text-slate-950">Cash in vs cash out</h2>
        <div className="inline-flex h-9 items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
          {["monthly", "weekly", "daily"].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onGranularityChange(option)}
              className={`h-7 rounded-md px-3 text-[12px] font-bold capitalize ${granularity === option ? "bg-white text-slate-950 shadow-sm" : "text-slate-500"}`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-5 space-y-4">
        {rows.map((row) => {
          const cashIn = Number(row.cash_in_centavos || 0);
          const cashOut = Number(row.cash_out_centavos || 0);
          const net = Number(row.net_centavos || 0);
          return (
            <div key={row.label || "period"} className="grid gap-2 md:grid-cols-[110px_1fr_110px] md:items-center">
              <div className="text-[12px] font-bold text-slate-500">{row.label}</div>
              <div className="space-y-1">
                <div className="h-4 rounded-full bg-slate-100"><div className="h-full rounded-full bg-teal-500" style={{ width: `${Math.max(2, (cashIn / max) * 100)}%` }} /></div>
                <div className="h-4 rounded-full bg-slate-100"><div className="h-full rounded-full bg-orange-400" style={{ width: `${Math.max(2, (cashOut / max) * 100)}%` }} /></div>
                <div className={`border-t-2 border-dashed ${net >= 0 ? "border-slate-400" : "border-rose-300"}`} />
              </div>
              <div className="text-right text-[12px] font-bold text-slate-600">{formatMoney(net, currency)} net</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function BreakdownPanel({ title, rows, currency }) {
  const colors = ["bg-teal-500", "bg-emerald-400", "bg-blue-500", "bg-orange-400", "bg-slate-400"];
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <h2 className="text-[16px] font-bold tracking-tight text-slate-950">{title}</h2>
      </div>
      <div className="mt-4 divide-y divide-slate-100">
        {(rows || []).map((row, index) => (
          <div key={row.key || row.label} className="flex items-center justify-between gap-3 py-2">
            <div className="inline-flex min-w-0 items-center gap-2 text-[13px] font-semibold text-slate-600">
              <span className={`h-2 w-2 rounded-full ${colors[index % colors.length]}`} />
              <span className="truncate">{row.label}</span>
            </div>
            <div className="text-[13px] font-bold tabular-nums text-slate-950">{formatMoney(row.amount_centavos, currency)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PdcSchedule({ payload, currency }) {
  if (!payload?.pdc_available) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-5 text-[13px] font-semibold text-slate-500">
        {payload?.pdc_placeholder || "PDC clearing schedule requires cleared_at on money_events — not yet available."}
      </section>
    );
  }
  const rows = Array.isArray(payload?.pdc_schedule) ? payload.pdc_schedule : [];
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="text-[16px] font-bold tracking-tight text-slate-950">PDC clearing schedule</h2>
      <div className="mt-4 divide-y divide-slate-100">
        {rows.length === 0 ? <div className="py-6 text-center text-[13px] font-semibold text-slate-500">No PDCs clearing in the next 30 days.</div> : rows.map((row) => {
          const days = Number(row.days_until_clearing || 0);
          return (
            <div key={row.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div>
                <div className="text-[14px] font-bold text-slate-900">{row.patient_name}</div>
                <div className="text-[12px] font-semibold text-slate-500">{row.bank_description} · {formatDate(row.clearing_date)}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[13px] font-bold">{formatMoney(row.amount_centavos, currency)}</span>
                <span className={`rounded-full px-2 py-1 text-[11px] font-bold ${days <= 45 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{days} days</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function FilterBar({ period, onPeriodChange }) {
  return (
    <div className="inline-flex min-h-10 max-w-full flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="reports-period-selector">
      {PERIOD_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          data-testid={`reports-period-${option.value}`}
          onClick={() => onPeriodChange(option.value)}
          className={`inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border px-3 text-[13px] font-semibold transition ${
            period === option.value
              ? "border-slate-300 bg-white text-slate-950 shadow-sm"
              : "border-transparent bg-transparent text-slate-600 hover:text-slate-950"
          }`}
        >
          {option.value === "custom" ? <CalendarDays className="h-3.5 w-3.5" strokeWidth={2.25} /> : null}
          {option.label}
        </button>
      ))}
    </div>
  );
}

function CustomRangeInputs({ customRange, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
      <DateInput
        label="From"
        testid="reports-custom-start"
        value={customRange.start}
        onChange={(start) => onChange((current) => ({ ...current, start }))}
      />
      <DateInput
        label="To"
        testid="reports-custom-end"
        value={customRange.end}
        onChange={(end) => onChange((current) => ({ ...current, end }))}
      />
    </div>
  );
}

function DateInput({ label, testid, value, onChange }) {
  return (
    <label className="flex items-center gap-2 text-[13px] font-semibold text-slate-600">
      {label}
      <input
        data-testid={testid}
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 rounded-md border border-slate-300 bg-white px-3 text-[13px] font-semibold text-slate-950 outline-none transition focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
      />
    </label>
  );
}

function WarningStrip({ count, amount, currency }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
      <AlertTriangle className="h-4 w-4" strokeWidth={2.25} />
      <span className="font-semibold">
        {count} patients have balances overdue 60+ days, {formatMoney(amount, currency)} total.
      </span>
      <a href="/reports/ar" className="font-bold underline underline-offset-2">View AR</a>
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="pt-1 text-[12px] font-bold uppercase tracking-wide text-slate-500">{children}</div>;
}

function KpiCard({ title, value, sub, tone }) {
  const toneClass = tone === "positive" ? "text-teal-700" : tone === "warning" ? "text-amber-800" : "text-slate-500";
  const cardClass = tone === "positive"
    ? "border-emerald-200 bg-emerald-50 text-emerald-950"
    : tone === "warning"
      ? "border-amber-200 bg-amber-50 text-amber-950"
      : "border-slate-200 bg-white text-slate-950";
  return (
    <article className={`min-h-[118px] rounded-lg border p-4 ${cardClass}`}>
      <div className="text-[12px] font-bold uppercase tracking-wide opacity-70">{title}</div>
      <div className="mt-2 text-[27px] font-bold leading-none tracking-normal tabular-nums">{value}</div>
      <div className={`mt-3 text-[12px] font-semibold ${toneClass}`}>{sub}</div>
    </article>
  );
}

function Panel({ title, action, children, testid, className = "" }) {
  return (
    <section data-testid={testid} className={`rounded-lg border border-slate-200 bg-white p-4 ${className}`}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <h2 className="text-[15px] font-bold">{title}</h2>
        {action ? <div className="text-[11px] font-medium text-slate-500">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

function ProductionByDoctor({ rows, currency }) {
  const list = sortedRows(rows, "production_centavos");
  const max = Math.max(...list.map((row) => Number(row.production_centavos || 0)), 1);
  return (
    <Panel title="Production by doctor" action={<SmallLink label="see details" />} testid="reports-production-doctor">
      <div className="space-y-3">
        {list.map((row) => {
          const width = `${Math.max(4, (Number(row.production_centavos || 0) / max) * 100)}%`;
          return (
            <div key={row.doctor_id || row.doctor_name} className="grid grid-cols-[96px_1fr_92px] items-center gap-3 text-[13px]">
              <div className="truncate">{row.doctor_name || "Doctor"}</div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full rounded-full bg-teal-600" style={{ width }} />
              </div>
              <div className="text-right font-bold tabular-nums">{formatMoney(row.production_centavos, currency)}</div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-[11px] text-slate-500">Production equals procedures completed. Not patient payment.</p>
    </Panel>
  );
}

function PaymentMethodMix({ rows }) {
  const list = Array.isArray(rows) ? rows : [];
  const largest = list[0] || {};
  return (
    <Panel title="Payment method mix" testid="reports-payment-method-mix">
      <div className="grid items-center gap-4 sm:grid-cols-[180px_1fr]">
        <Donut rows={list} center={`${largest.method || "None"} ${formatPercent(largest.pct)}%`} />
        <div className="space-y-3">
          {list.map((row, index) => (
            <div key={row.method} className="flex items-center justify-between gap-3 text-[13px]">
              <span className="inline-flex items-center gap-2">
                <span className={`h-3 w-3 rounded-full ${STREAM_COLORS[index % STREAM_COLORS.length]}`} />
                {row.method}
              </span>
              <span className="font-bold tabular-nums">{formatPercent(row.pct)}%</span>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
}

function Donut({ rows, center }) {
  let cursor = 0;
  const stops = (Array.isArray(rows) ? rows : []).map((row, index) => {
    const pct = Math.max(0, Number(row.pct || 0));
    const start = cursor;
    cursor += pct;
    return `${cssColor(index)} ${start}% ${cursor}%`;
  }).join(", ");
  return (
    <div className="relative mx-auto h-[150px] w-[150px] rounded-full" style={{ background: `conic-gradient(${stops || "oklch(0.88 0.01 80) 0 100%"})` }}>
      <div className="absolute inset-9 flex items-center justify-center rounded-full bg-slate-50 text-center text-[13px] font-bold leading-4">
        {center}
      </div>
    </div>
  );
}

function OpenPatientBalances({ rows, currency }) {
  const list = sortedRows(rows, "amount_centavos").slice(0, 5);
  return (
    <Panel title="Open patient balances" action={<SmallLink label="see all" />} testid="reports-open-balances">
      <div className="divide-y divide-slate-200">
        {list.map((row) => (
          <div key={row.patient_id || row.patient_name} className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
            <div>
              <div className="text-[13px] font-bold">{row.patient_name}</div>
              <div className="mt-0.5 text-[12px] text-slate-500">
                {formatDate(row.last_visit_date)} · {row.procedure_name || "Procedure"}
              </div>
              <AgeBadge state={row.treatment_plan_state} days={row.days_since_last_visit} />
            </div>
            <div className="text-right text-[13px] font-bold tabular-nums">{formatMoney(row.amount_centavos, currency)}</div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function AgeBadge({ state, days }) {
  if (String(state || "").toLowerCase() === "abandoned") {
    return <span className="mt-1 inline-flex rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold lowercase text-slate-700">abandoned</span>;
  }
  const age = Number(days || 0);
  const color = age >= 46 ? "bg-rose-100 text-rose-800" : age >= 15 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800";
  return <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${color}`}>{age} days overdue</span>;
}

function HmoReceivables({ rows, currency }) {
  const list = sortedRows(rows, "outstanding_centavos");
  return (
    <Panel title="HMO receivables by insurer" action={<SmallLink label="see aging" />} testid="reports-hmo-insurer">
      <div className="divide-y divide-slate-200">
        {list.map((row) => {
          const agingHigh = Number(row.avg_aging_days || 0) > 45;
          return (
            <div key={row.insurer_id || row.insurer_name} className="grid grid-cols-[1fr_auto] gap-3 py-2 first:pt-0 last:pb-0 text-[13px]">
              <div>
                <div className="font-bold">{row.insurer_name}</div>
                <div className={`text-[12px] ${agingHigh ? "text-amber-800" : "text-slate-500"}`}>
                  Avg aging: {formatPercent(row.avg_aging_days)} days
                </div>
              </div>
              <div className="font-bold tabular-nums text-teal-700">{formatMoney(row.outstanding_centavos, currency)}</div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-[11px] text-slate-500">Aging from claim submission date, not visit date. Write-offs tracked separately.</p>
    </Panel>
  );
}

function TreatmentPlanFunnel({ data, currency }) {
  const stages = Array.isArray(data?.stages) ? data.stages : [];
  const max = Math.max(...stages.map((stage) => Number(stage.count || 0)), 1);
  return (
    <Panel title="Treatment plan funnel" testid="reports-treatment-funnel">
      <div className="space-y-3">
        {stages.map((stage, index) => (
          <div key={stage.stage} className="grid grid-cols-[118px_1fr_48px] items-center gap-3 text-[13px]">
            <div>{stage.stage}</div>
            <div className="h-8 rounded-md bg-slate-200">
              <div className={`flex h-8 items-center rounded-md px-2 text-[12px] font-bold text-slate-950 ${index < 3 ? "bg-emerald-400" : index === 3 ? "bg-amber-400" : "bg-rose-300"}`} style={{ width: `${Math.max(8, Number(stage.count || 0) * 100 / max)}%` }}>
                {stage.count}
              </div>
            </div>
            <div className="text-right font-semibold tabular-nums">{formatPercent(stage.pct_of_proposed)}%</div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-3 text-[12px] text-slate-600">
        <span>Proposed value: <strong>{formatMoney(data?.proposed_value_centavos, currency)}</strong></span>
        <span>Case acceptance: <strong>{formatPercent(data?.case_acceptance_pct)}%</strong></span>
      </div>
    </Panel>
  );
}

function RecallRetention({ data }) {
  return (
    <Panel title="Recall & retention" testid="reports-recall-retention">
      <div className="rounded-lg bg-slate-100 p-4">
        <div className="text-[12px] text-slate-600">Patients due for recall (6-month)</div>
        <div className="mt-2 text-[32px] font-bold leading-none tabular-nums">{data?.recall_due_count || 0}</div>
        <div className="mt-2 text-[12px] text-slate-500">Not yet contacted</div>
      </div>
      <MetricRows rows={[
        ["Recall contact rate", `${formatPercent(data?.recall_contact_rate_pct)}%`],
        ["Recall to booked", `${formatPercent(data?.recall_to_booked_rate_pct)}%`],
        ["No-show rate this period", `${formatPercent(data?.no_show_rate_pct)}%`],
        ["Avg visits per active patient", formatPercent(data?.avg_visits_per_active_patient)],
      ]} />
    </Panel>
  );
}

function OrthodonticsSection({ data, currency }) {
  const pipeline = data?.contract_pipeline || {};
  return (
    <section data-testid="reports-orthodontics" className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <KpiCard title="Active ortho patients" value={formatNumber(data?.active_patients_count)} sub="Brackets on, in-program" />
        <KpiCard title="Monthly adjustment revenue" value={formatMoney(data?.monthly_adjustment_revenue_centavos, currency)} sub="+4% vs last period" tone="positive" />
        <KpiCard title="Ortho actual AR" value={formatMoney(data?.ar_centavos, currency)} sub="Unpaid completed work" />
        <KpiCard title="Dropout risk flagged" value={formatNumber(data?.dropout_risk_count)} sub="60+ days past adjustment" tone="warning" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <OrthoStreams rows={data?.revenue_streams} currency={currency} />
        <OrthoPatientStatus rows={data?.patient_status} currency={currency} />
      </div>
      <Panel title="Ortho contract pipeline" action={<SmallLink label="see breakdown" />} testid="reports-ortho-pipeline">
        <div className="grid gap-4 md:grid-cols-5">
          <MiniStat label="Records done" value={formatNumber(pipeline.pre_bonding_count)} sub="Pre-bonding patients" />
          <MiniStat label="Bonded this period" value={formatNumber(pipeline.new_bracket_placements_count)} sub="New bracket placements" />
          <MiniStat label="Avg contract value" value={formatMoney(pipeline.avg_contract_value_centavos, currency)} sub="Across active cases" />
          <MiniStat label="Projected program revenue" value={formatMoney(pipeline.projected_remaining_revenue_centavos, currency)} sub="Remaining on live contracts" good />
          <MiniStat label="Dropout loss (est.)" value={formatMoney(pipeline.estimated_dropout_loss_centavos, currency)} sub="At-risk remaining" warn />
        </div>
      </Panel>
    </section>
  );
}

function OrthoStreams({ rows, currency }) {
  const list = Array.isArray(rows) ? rows : [];
  const max = Math.max(...list.map((row) => Number(row.amount_centavos || 0)), 1);
  return (
    <Panel title="Ortho revenue streams" testid="reports-ortho-streams">
      <div className="space-y-3">
        {list.map((row) => (
          <div key={row.key || row.label} className="grid grid-cols-[128px_1fr_88px] items-center gap-3 text-[13px]">
            <div className="truncate">{row.label}</div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-200">
              <div className="h-full rounded-full bg-violet-500" style={{ width: `${Math.max(4, Number(row.amount_centavos || 0) * 100 / max)}%` }} />
            </div>
            <div className="text-right font-bold">{formatMoney(row.amount_centavos, currency)}</div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-[11px] text-slate-500">Four billable streams tracked separately. Retainer delivery is end-of-program.</p>
    </Panel>
  );
}

function OrthoPatientStatus({ rows, currency }) {
  const list = Array.isArray(rows) ? rows : [];
  return (
    <Panel title="Ortho patient status" action={<SmallLink label="see all" />} testid="reports-ortho-status">
      <div className="divide-y divide-slate-200">
        {list.map((row) => (
          <div key={row.patient_id || row.patient_name} className="grid grid-cols-[1fr_auto] gap-3 py-3 first:pt-0 last:pb-0 text-[13px]">
            <div>
              <div className="font-bold">{row.patient_name}</div>
              <div className="text-[12px] text-slate-500">
                Month {row.treatment_month || 0} of {row.estimated_total_months || 0} · {row.doctor_name}
              </div>
              <StatusBadge label={row.status_badge} />
            </div>
            <div className="text-right font-bold tabular-nums">
              {Number(row.balance_centavos || 0) > 0
                ? `${formatMoney(row.balance_centavos, currency)} AR`
                : `${formatMoney(row.payment_per_adjustment_centavos, currency)}/mo`}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function DoctorCommissions({ rows, currency, breakdownPath }) {
  const list = Array.isArray(rows) ? rows : [];
  const payoutBasis = list.find((row) => row?.payout_basis)?.payout_basis || "on_collection";
  const basisCopy = payoutBasis === "on_completion"
    ? "Commission is payable when procedures are completed. Production reports are unchanged by payment status."
    : "Commission is based on confirmed patient payments. AR is excluded; missing rates are flagged without hiding known earned commission.";
  return (
    <Panel title="Commission summary this period" action={<SmallLink label="see breakdown" to={breakdownPath} testid="reports-commissions-breakdown-link" />} testid="reports-commissions">
      <div className="grid gap-4 md:grid-cols-4">
        {list.map((row) => {
          const hasKnownCommission = row.commission_centavos !== null && row.commission_centavos !== undefined;
          return (
            <div key={row.doctor_id || row.doctor_name}>
              <div className="text-[13px] font-medium">{row.doctor_name}</div>
              <div className="mt-2 text-[22px] font-bold tabular-nums">
                {hasKnownCommission ? formatMoney(row.commission_centavos, currency) : "Missing rate"}
              </div>
              <div className={`mt-1 inline-flex items-center gap-1 text-[12px] font-semibold ${row.missing_rate ? "text-amber-800" : "text-slate-500"}`}>
                {row.missing_rate ? <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.25} /> : null}
                {row.rate_label}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 border-t border-slate-200 pt-3 text-[11px] text-slate-500">
        {basisCopy}
      </p>
    </Panel>
  );
}

function ProcedureMix({ rows, currency }) {
  const list = sortedRows(rows, "production_centavos");
  const max = Math.max(...list.map((row) => Number(row.production_centavos || 0)), 1);
  return (
    <Panel title="Revenue by procedure category" testid="reports-procedure-mix">
      <div className="space-y-3">
        {list.map((row, index) => (
          <div key={row.category} className="grid grid-cols-[128px_1fr_92px] items-center gap-3 text-[13px]">
            <div>{row.category}</div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-200">
              <div className={`h-full rounded-full ${STREAM_COLORS[index % STREAM_COLORS.length]}`} style={{ width: `${Math.max(4, Number(row.production_centavos || 0) * 100 / max)}%` }} />
            </div>
            <div className="text-right font-bold">{formatMoney(row.production_centavos, currency)}</div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function MetricRows({ rows }) {
  return (
    <div className="mt-4 divide-y divide-slate-200">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between gap-3 py-2 text-[13px]">
          <span>{label}</span>
          <span className="font-bold tabular-nums">{value}</span>
        </div>
      ))}
    </div>
  );
}

function MiniStat({ label, value, sub, good, warn }) {
  return (
    <div>
      <div className="text-[12px] text-slate-600">{label}</div>
      <div className={`mt-2 text-[22px] font-bold leading-none tabular-nums ${good ? "text-teal-700" : warn ? "text-amber-800" : ""}`}>{value}</div>
      <div className="mt-2 text-[11px] text-slate-500">{sub}</div>
    </div>
  );
}

function CommissionBreakdown({ activeClinic }) {
  const initialFilters = useMemo(() => readReportFiltersFromUrl(), []);
  const { activeBranchId, loading: branchLoading } = useBranch();
  const branchId = activeBranchId || "";
  const [period, setPeriod] = useState(initialFilters.period);
  const [customRange, setCustomRange] = useState(initialFilters.customRange);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeDoctorId, setActiveDoctorId] = useState(null);
  const [pageByDoctor, setPageByDoctor] = useState({});

  const effectivePeriod = useMemo(() => {
    if (period !== "custom") return period;
    return `custom:${customRange.start}:${customRange.end}`;
  }, [customRange.end, customRange.start, period]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (!branchId) {
      setPayload(null);
      setLoading(Boolean(branchLoading));
      setError(branchLoading ? null : "No active branch selected.");
      return;
    }
    const { data, error: rpcError } = await supabase.rpc("reports_commission_breakdown", {
      p_period: effectivePeriod,
      p_branch_id: branchId,
    });
    if (rpcError) {
      setError(rpcError.message || "Could not load commission breakdown.");
      setLoading(false);
      return;
    }
    setPayload(data || {});
    setLoading(false);
  }, [branchId, branchLoading, effectivePeriod]);

  useEffect(() => { load(); }, [load]);

  const doctors = useMemo(() => Array.isArray(payload?.doctors) ? payload.doctors : [], [payload?.doctors]);

  useEffect(() => {
    if (!doctors.length) {
      setActiveDoctorId(null);
      return;
    }
    setActiveDoctorId((current) => doctors.some((doctor) => doctor.doctor_id === current) ? current : doctors[0].doctor_id);
  }, [doctors]);

  const currency = payload?.currency || activeClinic?.currency || "PHP";
  const activeDoctor = doctors.find((doctor) => doctor.doctor_id === activeDoctorId) || doctors[0] || null;
  const rows = useMemo(() => sortCommissionRows(activeDoctor?.rows), [activeDoctor?.rows]);
  const currentPage = Math.min(
    pageByDoctor[activeDoctor?.doctor_id] || 1,
    Math.max(1, Math.ceil(rows.length / COMMISSION_PAGE_SIZE))
  );
  const totalPages = Math.max(1, Math.ceil(rows.length / COMMISSION_PAGE_SIZE));
  const visibleRows = rows.slice((currentPage - 1) * COMMISSION_PAGE_SIZE, currentPage * COMMISSION_PAGE_SIZE);
  const backPath = buildReportPath("/reports", period, branchId);

  const selectDoctor = (doctorId) => {
    setActiveDoctorId(doctorId);
    setPageByDoctor((current) => ({ ...current, [doctorId]: 1 }));
  };

  const changePage = (direction) => {
    if (!activeDoctor?.doctor_id) return;
    setPageByDoctor((current) => {
      const next = Math.min(Math.max(1, currentPage + direction), totalPages);
      return { ...current, [activeDoctor.doctor_id]: next };
    });
  };

  const missingCount = Number(activeDoctor?.missing_rates_count || 0);

  if (loading && !payload) {
    return (
      <div data-testid="commission-breakdown-loading" className="rounded-lg border border-slate-200 bg-slate-50 p-5 text-[13px] text-slate-500">
        Loading commission breakdown...
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="commission-breakdown-error" className="rounded-lg border border-rose-200 bg-rose-50 p-5 text-[13px] text-rose-700">
        <div className="flex items-center gap-2 font-bold">
          <AlertTriangle className="h-4 w-4" strokeWidth={2.25} />
          Commission breakdown could not load
        </div>
        <p className="mt-1">{error}</p>
      </div>
    );
  }

  return (
    <main data-testid="commission-breakdown-page" className="mx-auto max-w-[1180px] space-y-5 text-slate-950">
      <header className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Link
              to={backPath}
              data-testid="commission-breakdown-back-link"
              className="mb-2 inline-flex items-center gap-1 text-[13px] font-semibold text-slate-600 transition hover:text-teal-700"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={2.25} />
              Back to reports
            </Link>
            <p className="text-[13px] font-semibold text-slate-500">
              {activeClinic?.clinic_name || activeClinic?.name || "Clinic"} · {payload?.period_label || "Month to date"}
            </p>
            <h1 className="mt-1 text-[30px] font-bold tracking-tight text-slate-950">Commission breakdown</h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <FilterBar
            period={period}
            onPeriodChange={(nextPeriod) => {
              setPeriod(nextPeriod);
              setPageByDoctor({});
            }}
          />
          <p className="text-[12px] font-semibold text-slate-500">{payload?.period_label || "Month to date"}</p>
        </div>
      </header>

      {period === "custom" ? (
        <CustomRangeInputs customRange={customRange} onChange={setCustomRange} />
      ) : null}

      <section className="rounded-lg border border-slate-200 bg-white" data-testid="commission-breakdown-panel">
        <div className="flex gap-2 overflow-x-auto border-b border-slate-200 px-4">
          {doctors.map((doctor) => {
            const isActive = doctor.doctor_id === activeDoctor?.doctor_id;
            const hasMissingRates = Number(doctor.missing_rates_count || 0) > 0;
            return (
              <button
                key={doctor.doctor_id || doctor.doctor_name}
                type="button"
                data-testid={`commission-doctor-tab-${doctor.doctor_id}`}
                onClick={() => selectDoctor(doctor.doctor_id)}
                className={`inline-flex min-h-12 items-center gap-1.5 border-b-2 px-2 text-[13px] font-bold transition ${
                  isActive
                    ? "border-teal-600 text-slate-950"
                    : "border-transparent text-slate-500 hover:text-slate-950"
                }`}
              >
                {doctor.doctor_name || "Doctor"}
                {hasMissingRates ? <AlertTriangle className="h-3.5 w-3.5 text-amber-700" strokeWidth={2.25} /> : null}
              </button>
            );
          })}
        </div>

        {activeDoctor ? (
          <div className="space-y-4 p-4">
            <div className="grid gap-3 md:grid-cols-3">
              <BreakdownSummaryCard title="Total earned" value={formatMoney(activeDoctor.total_earned_centavos, currency)} />
              <BreakdownSummaryCard title="Procedures counted" value={formatNumber(activeDoctor.procedures_counted)} />
              <BreakdownSummaryCard title="Missing rates" value={formatNumber(missingCount)} warning={missingCount > 0} />
            </div>

            {missingCount > 0 ? (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-semibold text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.25} />
                <span>{missingCount} {missingCount === 1 ? "procedure has" : "procedures have"} no commission rate set and are excluded from the total.</span>
              </div>
            ) : null}

            {rows.length ? (
              <>
                <CommissionRowsTable rows={visibleRows} currency={currency} />
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-3">
                  <p className="text-[13px] font-semibold text-slate-500">Page {currentPage} of {totalPages}</p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="commission-page-prev"
                      onClick={() => changePage(-1)}
                      disabled={currentPage === 1}
                      className="inline-flex h-9 items-center gap-1 rounded-md border border-slate-300 bg-white px-3 text-[13px] font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4" strokeWidth={2.25} />
                      Previous
                    </button>
                    <button
                      type="button"
                      data-testid="commission-page-next"
                      onClick={() => changePage(1)}
                      disabled={currentPage === totalPages}
                      className="inline-flex h-9 items-center gap-1 rounded-md border border-slate-300 bg-white px-3 text-[13px] font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" strokeWidth={2.25} />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-[13px] font-semibold text-slate-500">
                No confirmed payments recorded for this period.
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-3">
              <p className="text-[12px] font-medium text-slate-500">Based on confirmed patient payments. AR excluded.</p>
              <button
                type="button"
                data-testid="commission-export-csv"
                onClick={() => guardedCommissionExport(activeDoctor, rows, currency)}
                className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-[13px] font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <Download className="h-4 w-4" strokeWidth={2.25} />
                Export
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4">
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-[13px] font-semibold text-slate-500">
              No confirmed payments recorded for this period.
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

function BreakdownSummaryCard({ title, value, warning = false }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="text-[12px] font-bold uppercase tracking-wide text-slate-500">{title}</div>
      <div className={`mt-2 text-[28px] font-bold leading-none tabular-nums ${warning ? "text-amber-800" : "text-slate-950"}`}>{value}</div>
    </div>
  );
}

function CommissionRowsTable({ rows, currency }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-separate border-spacing-0 text-[13px]">
        <thead>
          <tr className="text-left text-[12px] font-bold uppercase tracking-wide text-slate-500">
            <th className="border-b border-slate-200 py-3 pr-4">Procedure</th>
            <th className="border-b border-slate-200 px-4 py-3">Patient</th>
            <th className="border-b border-slate-200 px-4 py-3">Date</th>
            <th className="border-b border-slate-200 px-4 py-3 text-right">Payment</th>
            <th className="border-b border-slate-200 px-4 py-3 text-right">Rate</th>
            <th className="border-b border-slate-200 py-3 pl-4 text-right">Commission</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const missingRate = row.missing_rate || row.commission_rate_snapshot === null || row.commission_rate_snapshot === undefined;
            return (
              <tr key={row.id || `${row.procedure_name}-${row.patient_name}-${row.paid_at}`}>
                <td className="border-b border-slate-100 py-3 pr-4 font-semibold text-slate-950">{row.procedure_name || "Procedure"}</td>
                <td className="border-b border-slate-100 px-4 py-3 text-slate-600">{row.patient_name || "Patient"}</td>
                <td className="border-b border-slate-100 px-4 py-3 text-slate-600">{formatDateTime(row.paid_at)}</td>
                <td className="border-b border-slate-100 px-4 py-3 text-right font-semibold tabular-nums">{formatMoney(row.payment_centavos, currency)}</td>
                <td className="border-b border-slate-100 px-4 py-3 text-right text-slate-600">{missingRate ? "—" : `${formatPercent(Number(row.commission_rate_snapshot || 0) * 100)}%`}</td>
                <td className="border-b border-slate-100 py-3 pl-4 text-right font-bold tabular-nums">
                  {missingRate
                    ? <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">No rate</span>
                    : formatMoney(row.commission_centavos, currency)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SmallLink({ label, to, testid }) {
  const content = (
    <span className="inline-flex items-center gap-1">
      {label}
      <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
    </span>
  );
  if (to) {
    return (
      <Link data-testid={testid} to={to} className="hover:text-slate-950">
        {content}
      </Link>
    );
  }
  return content;
}

function StatusBadge({ label }) {
  const normalized = String(label || "On Track");
  const tone = normalized === "Dropout Risk"
    ? "bg-rose-100 text-rose-800"
    : normalized === "Balance Lag"
      ? "bg-amber-100 text-amber-800"
      : normalized === "Near Completion"
        ? "bg-emerald-100 text-emerald-800"
        : "bg-blue-100 text-blue-800";
  return <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold lowercase ${tone}`}>{normalized}</span>;
}

function sortedRows(rows, key) {
  return Array.isArray(rows)
    ? [...rows].sort((a, b) => Number(b?.[key] || 0) - Number(a?.[key] || 0))
    : [];
}

function formatMoney(value, currency = "PHP") {
  const number = Math.round(Number(value || 0) / 100);
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(number);
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("en-PH", { maximumFractionDigits: 0 });
}

function formatPercent(value) {
  const number = Number(value || 0);
  return number.toLocaleString("en-PH", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  });
}

function formatDate(value) {
  if (!value) return "No visit";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}

function formatDateTime(value) {
  if (!value) return "No date";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}

function buildDefaultCustomRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    start: toDateInputValue(start),
    end: toDateInputValue(now),
  };
}

function readReportFiltersFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const rawPeriod = params.get("period") || "mtd";
  const customRange = buildDefaultCustomRange();
  if (rawPeriod.startsWith("custom:")) {
    const [, start, end] = rawPeriod.split(":");
    return {
      period: "custom",
      branchId: params.get("branch") || "",
      customRange: {
        start: start || customRange.start,
        end: end || customRange.end,
      },
    };
  }
  const allowedPeriods = new Set(PERIOD_OPTIONS.map((option) => option.value));
  return {
    period: allowedPeriods.has(rawPeriod) ? rawPeriod : "mtd",
    branchId: params.get("branch") || "",
    customRange,
  };
}

function buildReportPath(path, period, branchId) {
  const params = new URLSearchParams();
  params.set("period", period || "mtd");
  if (branchId) params.set("branch", branchId);
  return `${path}?${params.toString()}`;
}

function sortCommissionRows(rows) {
  return Array.isArray(rows)
    ? [...rows].sort((a, b) => new Date(b.paid_at || 0).getTime() - new Date(a.paid_at || 0).getTime())
    : [];
}

async function guardedCommissionExport(doctor, rows, currency) {
  const { error } = await supabase.rpc("require_data_export_aal2");
  if (error) {
    window.alert?.(error.message || "Two-factor authentication is required before exporting data.");
    return null;
  }
  return exportCommissionCsv(doctor, rows, currency);
}

function exportCommissionCsv(doctor, rows, currency) {
  const header = ["Procedure", "Patient", "Date", "Payment", "Rate", "Commission"];
  const body = rows.map((row) => {
    const missingRate = row.missing_rate || row.commission_rate_snapshot === null || row.commission_rate_snapshot === undefined;
    return [
      row.procedure_name || "",
      row.patient_name || "",
      formatDateTime(row.paid_at),
      formatMoney(row.payment_centavos, currency),
      missingRate ? "No rate" : `${formatPercent(Number(row.commission_rate_snapshot || 0) * 100)}%`,
      missingRate ? "" : formatMoney(row.commission_centavos, currency),
    ];
  });
  const csv = [header, ...body]
    .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");

  if (typeof window === "undefined" || !window.URL?.createObjectURL) return csv;

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `commission-breakdown-${doctor?.doctor_name || "doctor"}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return csv;
}

function toDateInputValue(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function cssColor(index) {
  const colors = [
    "oklch(0.56 0.12 166)",
    "oklch(0.73 0.11 155)",
    "oklch(0.62 0.16 250)",
    "oklch(0.72 0.16 72)",
    "oklch(0.68 0.02 80)",
    "oklch(0.62 0.17 294)",
  ];
  return colors[index % colors.length];
}
